/* eslint-disable no-nested-ternary */
/* eslint-disable no-underscore-dangle */
import { LoanProjectionChartDataset } from '@/loan/types/loan-projection-chart-datatset';
import { LoanProjectionPeriod } from '@/loan/types/loan-projection-period';
import { LoanProjectionChartDatasetPointStyles } from '@/loan/types/loan-projection-point-styles';
import Chart, { ChartTooltipModel } from 'chart.js';
import { UtilHelper } from '@/util/helper';

const PAYOFF_DIALOG_CONTAINER_CLASS = 'payoff-dialog-container';

export class LoanProjectionChartService {
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private constructor() {}

	public static calculateMaxYAxesAmount(periods: LoanProjectionPeriod[]): number {
		const highestBalance = periods.reduce((max, curr) => {
			const currentExpectedBalance = Number(curr.expectedEndingPrincipalBalance);
			const currentActualBalance = Number(curr.actualEndingPrincipalBalance);
			const highestCurrentBalance = currentExpectedBalance > currentActualBalance
				? currentExpectedBalance
				: currentActualBalance;

			return highestCurrentBalance > max ? highestCurrentBalance : max;
		}, 0);

		const ceiling = Math.ceil(highestBalance + 1000);

		return (Math.round(ceiling / 1000) * 1000);
	}

	public static createChartDataset(dataset: LoanProjectionChartDataset): Chart.ChartDataSets {
		const pointStyles = LoanProjectionChartService.createChartDatasetPointStyles(dataset.data);

		return {
			borderWidth: 0,
			lineTension: 0,
			spanGaps: true,
			...pointStyles,
			...dataset,
		};
	}

	public static createChartDatasetPointStyles(
		data: (number | null)[],
	): LoanProjectionChartDatasetPointStyles {
		return data.reduce((styles) => {
			styles.pointBackgroundColor.push('transparent');
			styles.pointBorderColor.push('transparent');
			styles.pointRadius.push(0);

			return styles;
		}, {
			pointBackgroundColor: [] as string[],
			pointBorderColor: [] as string[],
			pointRadius: [] as number[],
		});
	}

	public static createOriginalChartInstance({
		canvasContext,
		dataset,
		maxBalanceAmount,
		periods,
	}: {
		canvasContext: CanvasRenderingContext2D;
		dataset: Chart.ChartDataSets;
		maxBalanceAmount: number;
		periods: LoanProjectionPeriod[];
	}): Chart {
		return new Chart(canvasContext, {
			type: 'line',
			data: {
				labels: LoanProjectionChartService.createOriginalChartLabels(periods),
				datasets: [dataset],
			},
			options: {
				animation: {
					duration: 0,
					easing: 'linear',
				},
				responsive: true,
				maintainAspectRatio: false,
				legend: {
					display: false,
				},
				scales: {
					xAxes: [{
						gridLines: {
							drawOnChartArea: false,
						},
						ticks: {
							autoSkip: false,
							callback: LoanProjectionChartService.xAxesCallBack,
							maxRotation: 0,
							minRotation: 0,
						},
					}],
					yAxes: [{
						ticks: {
							callback: LoanProjectionChartService.yAxesCallBack(maxBalanceAmount),
							max: maxBalanceAmount,
							min: 0,
							stepSize: Math.floor(maxBalanceAmount / 4),
						},
					}],
				},
			},
		});
	}

	public static createOriginalChartLabels(periods: LoanProjectionPeriod[]): string[] {
		return periods.map((p) => {
			const date = new Date(p.endDate);
			const month = date.getMonth();
			const year = date.getFullYear();

			if (periods.length <= 60) {
				return month === 0
					? year.toString()
					: 'TICK';
			}

			switch (month) {
				case 0:
					return year.toString();
				case 3:
				case 6:
				case 9:
					return 'TICK';
				default:
					return 'OMIT';
			}
		});
	}

	public static createGradient({
		canvasContext,
		darkColor,
		lightColor,
	}: {
		canvasContext: CanvasRenderingContext2D;
		darkColor: string;
		lightColor: string;
	}): CanvasGradient {
		const gradient = canvasContext.createLinearGradient(0, 0, 0, 600);

		gradient.addColorStop(0, lightColor);
		gradient.addColorStop(0.333, lightColor);
		gradient.addColorStop(0.66, darkColor);
		gradient.addColorStop(1, darkColor);

		return gradient;
	}

	public static createPeriodDateLabel(period: LoanProjectionPeriod): string {
		const date = new Date(period.endDate);

		return `${date.getFullYear()}`;
	}

	public static createProjectionChartInstance({
		canvasContext,
		currentPeriodIndex,
		futureDataset,
		historicalDataset,
		maxBalanceAmount,
		periods,
		startBalance,
	}: {
		canvasContext: CanvasRenderingContext2D;
		currentPeriodIndex: number;
		futureDataset: Chart.ChartDataSets;
		maxBalanceAmount: number;
		historicalDataset: Chart.ChartDataSets;
		periods: LoanProjectionPeriod[];
		startBalance: number;
	}): Chart {
		return new Chart(canvasContext, {
			type: 'projectionChart',
			data: {
				labels: LoanProjectionChartService.createProjectionChartLabels(periods),
				datasets: [historicalDataset, futureDataset],
			},
			options: {
				animation: {
					duration: 0,
					easing: 'linear',
				},
				responsive: true,
				maintainAspectRatio: false,
				legend: {
					display: false,
				},
				hover: {
					mode: 'index',
					intersect: true,
				},
				scales: {
					xAxes: [{
						ticks: {
							autoSkip: false,
							callback: LoanProjectionChartService.xAxesCallBack,
							fontColor: 'transparent',
							maxRotation: 0,
							minRotation: 0,
						},
					}],
					yAxes: [{
						ticks: {
							callback: LoanProjectionChartService.yAxesCallBack(maxBalanceAmount),
							fontColor: 'transparent',
							max: maxBalanceAmount,
							min: 0,
							stepSize: Math.floor(maxBalanceAmount / 4),
						},
					}],
				},
				tooltips: {
					enabled: false,
					mode: 'index',
					intersect: false,
					custom: (tooltipModel: ChartTooltipModel) => {
						LoanProjectionChartService.drawTooltip({
							canvasContext,
							currentPeriodIndex,
							periods,
							startBalance,
							tooltipModel,
						});
					},
				},
			},
		});
	}

	public static createProjectionChartLabels(periods: LoanProjectionPeriod[]): string[] {
		return periods.map((p) => {
			const date = new Date(p.endDate);
			const month = date.getMonth();

			if (month === 0) {
				return date.getFullYear().toString();
			}

			return 'OMIT';
		});
	}

	public static drawCurrentTimePeriodLine(
		chartScope: Chart,
		label = 'Historical',
	): void {
		try {
			const chartDataSets = chartScope.data.datasets;
			const historicalDataset = chartDataSets?.find((d) => d.label === label);

			if (!historicalDataset?.data) {
				throw new Error('Historical Dataset is undefined');
			}

			const metaProp = Object.keys((historicalDataset as any)._meta)[0];
			const lastHistoricalDatasetIndex: number = historicalDataset.data.length - 1;
			const lastHistoricalDatasetData = (historicalDataset as any)._meta[metaProp].data;
			const lastHistoricalDatasetChartElement = lastHistoricalDatasetData[lastHistoricalDatasetIndex];
			const { x, y } = lastHistoricalDatasetChartElement._model;
			const { ctx } = chartScope as any;
			const bottomY = chartScope.chartArea.bottom;

			LoanProjectionChartService.drawLineWithPoint({
				bottomY,
				canvasContext: ctx,
				x,
				y,
			});
		} catch (error) {
			console.error(error);
		}
	}

	public static drawHoveredTimePeriodLine(chartScope: any): void {
		try {
			if (chartScope?.tooltip?._active && chartScope.tooltip._active.length) {
				const activePoint = chartScope.tooltip._active[0];
				const activePointModel = activePoint._model;
				const { ctx } = chartScope;
				const { x } = activePoint.tooltipPosition();
				const bottomY = chartScope.chartArea.bottom;

				LoanProjectionChartService.drawLineWithPoint({
					bottomY,
					canvasContext: ctx,
					x,
					y: activePointModel.y,
				});
			}
		} catch (error) {
			console.error(error);
		}
	}

	public static drawLine({
		canvasContext,
		x,
		y,
		bottomY,
	}: {
		canvasContext: CanvasRenderingContext2D;
		x: number;
		y: number;
		bottomY: number;
	}): void {
		canvasContext.save();
		canvasContext.beginPath();
		canvasContext.moveTo(x, y);
		canvasContext.lineTo(x, bottomY);
		canvasContext.lineWidth = 2;
		canvasContext.strokeStyle = '#47505e';
		canvasContext.stroke();
		canvasContext.restore();
		canvasContext.fillStyle = '#47505e';
	}

	public static drawLineWithPoint({
		canvasContext,
		x,
		y,
		bottomY,
	}: {
		canvasContext: CanvasRenderingContext2D;
		x: number;
		y: number;
		bottomY: number;
	}): void {
		LoanProjectionChartService.drawLine({
			canvasContext, x, y, bottomY,
		});

		Chart.helpers.canvas.drawPoint(canvasContext, {}, 5, x, y, 0);
	}

	public static drawTooltip = ({
		canvasContext,
		currentPeriodIndex,
		periods,
		startBalance,
		tooltipModel,
	}: {
		canvasContext: CanvasRenderingContext2D;
		currentPeriodIndex: number;
		periods: LoanProjectionPeriod[];
		startBalance: number;
		tooltipModel: ChartTooltipModel;
	}): void => {
		let tooltipElement = document.getElementById('chartjs-tooltip');

		// Create element on first render
		if (!tooltipElement) {
			tooltipElement = document.createElement('div');
			tooltipElement.id = 'chartjs-tooltip';
			tooltipElement.classList.add('projection-chart-tooltip');
			document.body.appendChild(tooltipElement);
		}

		// Hide if no tooltip
		if (tooltipModel.opacity === 0) {
			tooltipElement.style.opacity = '0';
			return;
		}

		tooltipElement.innerHTML = '';

		const dataPoint = tooltipModel.dataPoints[0];
		const period = periods[dataPoint.index as number];
		const {
			actualEndingPrincipalBalance: balance,
			actualAmountPaidInPeriod: payments,
			endingAvailableToTakeBack: takeBack,
			actualAmountPaidInPeriod: amountPaid,
		} = period;

		const isHistoricalPeriod = (dataPoint?.index ?? 0) <= currentPeriodIndex;

		// If is a historical period or is after the last payment in the periods then don't display the payment tooltip
		if (!isHistoricalPeriod && Number(amountPaid ?? 0) <= 0) {
			tooltipElement.style.opacity = '0';
			return;
		}

		const endDateISOString = period.endDate.split(' ')[0];
		const startDateISOString = period.startDate.split(' ')[0];
		const currentHoveredPeriodIndex = dataPoint.index;
		const lastPeriodIndex = periods.length - 1;
		const isFirstPeriod = currentHoveredPeriodIndex === 0;
		const isLastPeriod = currentHoveredPeriodIndex === lastPeriodIndex;

		const titleElement = document.createElement('div');
		const titlePrefix = isFirstPeriod ? 'Loan Start' : (isLastPeriod ? 'Projected Payoff' : 'Through');
		const title = `${titlePrefix} ${UtilHelper.formatISOStringForDisplay(isFirstPeriod ? startDateISOString : endDateISOString)}`;
		titleElement.classList.add('projection-chart-tooltip-title');
		titleElement.innerHTML = title;
		tooltipElement.append(titleElement);

		const balanceElement = document.createElement('div');
		balanceElement.classList.add('projection-chart-tooltip-balance');

		const balanceLabelElement = document.createElement('div');
		balanceLabelElement.classList.add('projection-chart-tooltip-balance-label');
		balanceLabelElement.innerText = isFirstPeriod ? 'Starting Balance' : 'Ending Balance';
		balanceElement.append(balanceLabelElement);

		const balanceValueElement = document.createElement('div');
		balanceValueElement.classList.add('projection-chart-tooltip-balance-value');
		balanceValueElement.innerText = `${UtilHelper.formatDollar(isFirstPeriod ? startBalance : balance)}`;
		balanceElement.append(balanceValueElement);
		tooltipElement.append(balanceElement);

		if (!isFirstPeriod && !isLastPeriod) {
			const totalPaymentsElement = document.createElement('div');
			const totalPaymentsValueElement = document.createElement('div');

			totalPaymentsElement.classList.add('projection-chart-tooltip-principal');
			totalPaymentsElement.append('Total Payments');
			totalPaymentsValueElement.append(UtilHelper.formatDollar(payments));
			totalPaymentsElement.append(totalPaymentsValueElement);
			tooltipElement.append(totalPaymentsElement);

			const takeBackElement = document.createElement('div');
			const takeBackValueElement = document.createElement('div');
			takeBackElement.classList.add('projection-chart-tooltip-interest');
			takeBackElement.append('Take-Back Balance');
			takeBackValueElement.append(UtilHelper.formatDollar(takeBack));
			takeBackElement.append(takeBackValueElement);
			tooltipElement.append(takeBackElement);
		}

		const canvasDimensions = canvasContext.canvas.getBoundingClientRect();

		tooltipElement.style.opacity = '1';
		tooltipElement.style.position = 'absolute';
		tooltipElement.style.zIndex = '300';
		tooltipElement.style.left = `${canvasDimensions.left + window.pageXOffset + tooltipModel.caretX}px`;
		tooltipElement.style.top = `${canvasDimensions.top + window.pageYOffset + tooltipModel.caretY}px`;
		tooltipElement.style.padding = `${tooltipModel.yPadding}px${tooltipModel.xPadding}px`;
		tooltipElement.style.pointerEvents = 'none';
	};

	public static getFutureChartDatasetData({
		futurePeriods,
		historicalPeriods,
	}: {
		futurePeriods: LoanProjectionPeriod[];
		historicalPeriods: LoanProjectionPeriod[];
	}): (number | null)[] {
		const data: (number | null)[] = futurePeriods.map((p) => Number(p.actualEndingPrincipalBalance));

		historicalPeriods.forEach(() => data.unshift(null));

		if (historicalPeriods.length > 0) {
			const datasetIdx = historicalPeriods.length - 1;
			data[datasetIdx] = Number(historicalPeriods[datasetIdx].actualEndingPrincipalBalance);
		}

		return data;
	}

	public static getFuturePeriods({
		periods,
		currentPeriodIndex,
	}: {
		periods: LoanProjectionPeriod[];
		currentPeriodIndex: number;
	}): LoanProjectionPeriod[] {
		return periods.slice(currentPeriodIndex + 1, periods.length);
	}

	public static getHistoricalChartDatasetData(
		periods: LoanProjectionPeriod[],
	): number[] {
		return periods.map((p) => Number(p.actualEndingPrincipalBalance));
	}

	public static getHistoricalPeriods({
		periods,
		currentPeriodIndex,
	}: {
		periods: LoanProjectionPeriod[];
		currentPeriodIndex: number;
	}): LoanProjectionPeriod[] {
		return periods.slice(0, currentPeriodIndex + 1);
	}

	public static getOriginalChartDatasetData(
		periods: LoanProjectionPeriod[],
	): number[] {
		return periods.map((p) => Number(p.expectedEndingPrincipalBalance));
	}

	public static setProjectionChartAsChartType({
		canvasContainer,
		periods,
		withPayoffDialog,
	}: {
		canvasContainer: HTMLElement;
		periods: LoanProjectionPeriod[];
		withPayoffDialog: boolean;
	}): void {
		const actualRunningInterest =	periods.reduce((sum, p) => sum + Number(p.actualInterestPaid), 0);

		const originalRunningInterest =	periods.reduce((sum, p) => sum + Number(p.expectedInterestPaid), 0);

		let interestAvoided = Number(originalRunningInterest) - Number(actualRunningInterest);

		if (interestAvoided < 0) 		{
			interestAvoided = 0;
		}

		let isDrawn = false;

		if (withPayoffDialog) {
			const payOffDialogContainer = this.createPayOffDialogContainer(canvasContainer);
			canvasContainer.appendChild(payOffDialogContainer);
		}

		Chart.defaults.projectionChart = Chart.defaults.line;

		Chart.controllers.projectionChart = Chart.controllers.line.extend({
			draw(ease: any) {
				Chart.controllers.line.prototype.draw.call(this, ease);
				LoanProjectionChartService.drawHoveredTimePeriodLine(this.chart);

				if (isDrawn) {
					LoanProjectionChartService.drawCurrentTimePeriodLine(this.chart);

					if (withPayoffDialog) {
						LoanProjectionChartService.renderPayoffDialog({
							chartScope: this.chart,
							periods,
							interestAvoided,
						});
					}

					return;
				}

				isDrawn = true;

				setTimeout(() => {
					LoanProjectionChartService.drawCurrentTimePeriodLine(this.chart);
				}, 600);
			},
		});
	}

	public static xAxesCallBack(label: string) {
		if (label === 'OMIT') {
			return null;
		}

		if (label === 'TICK') {
			return '';
		}

		return label;
	}

	public static yAxesCallBack(startBalance: number): (value: string | number) => string | number {
		return (value: string | number): string | number => {
			const balance = Math.floor(Number(value));

			if (
				balance === 0
				|| balance === Math.floor(startBalance)
				|| balance === Math.floor(startBalance / 2)
			) {
				return UtilHelper.formatDollar(balance);
			}

			return '';
		};
	}

	public static createPayOffDialogContainer(canvasContainer: HTMLElement): HTMLElement {
		let payOffDialogContainer = canvasContainer.querySelector(`.${PAYOFF_DIALOG_CONTAINER_CLASS}`) as HTMLElement;

		if (payOffDialogContainer) {
			payOffDialogContainer.remove();
		}

		payOffDialogContainer = document.createElement('div');
		payOffDialogContainer.classList.add(PAYOFF_DIALOG_CONTAINER_CLASS);

		return payOffDialogContainer;
	}

	public static renderPayoffDialog({
		chartScope,
		periods,
		interestAvoided,
	}: {
		chartScope: Chart;
		periods: LoanProjectionPeriod[];
		interestAvoided: number
	}): void {
		const payOffDialogContainer = document.querySelector(`.${PAYOFF_DIALOG_CONTAINER_CLASS}`) as HTMLElement;

		if (!payOffDialogContainer) {
			return;
		}

		const chartDataSets = chartScope.config.data?.datasets ?? [];
		const futureDataset = chartDataSets.find((d) => d.label === 'Future');

		if (!futureDataset) {
			return;
		}

		const { index, period } = LoanProjectionChartService.getPayOffPeriod(periods);
		const [payoffDate] = period?.endDate.split(' ') ?? '';

		const [metaProp] = Object.keys((futureDataset as any)._meta);
		const projectionDatasetData = (futureDataset as any)._meta[metaProp].data;
		const firstZeroBalanceDatasetChartElement = projectionDatasetData[index];
		const { x, y } = firstZeroBalanceDatasetChartElement._model;
		const dialogContainerElementDimensions = payOffDialogContainer.getBoundingClientRect();

		payOffDialogContainer.style.left = `${x - 125}px`;
		payOffDialogContainer.style.top = `${y - dialogContainerElementDimensions.height - 10}px`;

		payOffDialogContainer.innerHTML = `
				<div class="payoff-dialog">
					<div>Projected Payoff</div>
					<div class="payoff-date">
						${UtilHelper.formatISOStringForDisplay(payoffDate)}
					</div>
					<div>Interest Avoided</div>
					<div class="interest-avoided">
						${UtilHelper.formatDollar(interestAvoided)}
					</div>
				</div>
			`;
	}

	public static showPayoffDialog(canvasContainer: HTMLElement): void {
		if (!canvasContainer) {
			return;
		}

		const payOffDialogContainer = this.createPayOffDialogContainer(canvasContainer);
		canvasContainer.appendChild(payOffDialogContainer);

		payOffDialogContainer.style.visibility = 'visible';
	}

	public static getPayOffPeriod(
		periods: LoanProjectionPeriod[],
	): { period: LoanProjectionPeriod | undefined; index: number } {
		let index = 0;
		const period = periods.find((p, i) => {
			const found = Number(p.actualEndingPrincipalBalance) === 0;
			index = found ? i : index;

			return found;
		});

		return {
			index,
			period,
		};
	}
}
